import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { fmtDays } from './hrShared'

// การ์ดประเภทการลา 1 รายการ สำหรับมุมมองมือถือ
const LeaveTypeCard = ({ t, onEdit }) => (
  <div className="border rounded p-3" style={{ cursor: 'pointer' }} onClick={onEdit}>
    <div className="d-flex align-items-start justify-content-between gap-2">
      <div className="fw-semibold">{t.lt_name}</div>
      <Badge bg={t.lt_active ? 'success' : 'secondary'}>{t.lt_active ? 'ใช้งาน' : 'ปิดใช้งาน'}</Badge>
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <div><span className="text-muted">โควตา/ปี:</span> {t.lt_annual_quota === null ? 'ไม่จำกัด' : `${fmtDays(t.lt_annual_quota)} วัน`}</div>
      <div><span className="text-muted">ต้องแนบไฟล์:</span> {t.lt_require_attachment ? <i className="ri-check-line text-success" /> : '-'}</div>
      {t.lt_note && <div><span className="text-muted">หมายเหตุ:</span> {t.lt_note}</div>}
    </div>
  </div>
)

export default function HrLeaveTypes() {
  const [types, setTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const [modal, setModal] = useState(null) // null | {lt_id?, ...}
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchTypes = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get('/api/hr/leave-types')
      if (res.data.success) setTypes(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchTypes() }, [fetchTypes])

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setModalError('')
    try {
      await axiosInstance.post('/api/hr/leave-types', {
        ...modal,
        lt_annual_quota: modal.lt_annual_quota === '' ? null : modal.lt_annual_quota,
      })
      setModal(null)
      fetchTypes()
    } catch (err) {
      setModalError(err.response?.data?.message === 'DUPLICATE_DATA'
        ? 'มีประเภทการลาชื่อนี้อยู่แล้ว'
        : err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-list-settings-line me-2 text-primary" />
              ประเภทการลา
            </h4>
            <Button variant="primary" size="sm"
              onClick={() => { setModal({ lt_name: '', lt_annual_quota: '', lt_require_attachment: 0, lt_active: 1, lt_note: '' }); setModalError('') }}>
              <i className="ri-add-line me-1" />เพิ่มประเภทการลา
            </Button>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center justify-content-between gap-2">
              <Card.Title as="h6" className="mb-0">ประเภทการลาทั้งหมด</Card.Title>
              <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                  onClick={() => setView('table')} title="มุมมองตาราง">
                  <i className="ri-table-line" />
                </button>
                <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                  onClick={() => setView('card')} title="มุมมองการ์ด">
                  <i className="ri-layout-grid-line" />
                </button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : types.length === 0 ? (
                <div className="text-center text-muted py-4">ยังไม่มีประเภทการลา</div>
              ) : view === 'card' ? (
                <div className="p-3">
                  <Row className="g-3">
                    {types.map(t => (
                      <Col key={t.lt_id} xs={12} md={6} xl={4}>
                        <LeaveTypeCard t={t} onEdit={() => { setModal({ ...t, lt_annual_quota: t.lt_annual_quota ?? '' }); setModalError('') }} />
                      </Col>
                    ))}
                  </Row>
                </div>
              ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th>ชื่อประเภทการลา</th>
                      <th className="text-end">โควตา/ปี (วัน)</th>
                      <th className="text-center">ต้องแนบไฟล์</th>
                      <th className="text-center">สถานะ</th>
                      <th>หมายเหตุ</th>
                      <th style={{ width: 70 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {types.map(t => (
                      <tr key={t.lt_id}>
                        <td>{t.lt_name}</td>
                        <td className="text-end">{t.lt_annual_quota === null ? <span className="text-muted">ไม่จำกัด</span> : fmtDays(t.lt_annual_quota)}</td>
                        <td className="text-center">{t.lt_require_attachment ? <i className="ri-check-line text-success" /> : '-'}</td>
                        <td className="text-center">
                          <Badge bg={t.lt_active ? 'success' : 'secondary'}>{t.lt_active ? 'ใช้งาน' : 'ปิดใช้งาน'}</Badge>
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 220 }}>{t.lt_note || '-'}</td>
                        <td className="text-end">
                          <Button variant="link" size="sm" className="p-0"
                            onClick={() => { setModal({ ...t, lt_annual_quota: t.lt_annual_quota ?? '' }); setModalError('') }}>
                            <i className="ri-edit-line" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />

      <Modal show={!!modal} onHide={() => setModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">{modal?.lt_id ? 'แก้ไข' : 'เพิ่ม'}ประเภทการลา</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">ชื่อประเภทการลา</Form.Label>
              <Form.Control size="sm" required value={modal?.lt_name || ''}
                onChange={e => setModal(m => ({ ...m, lt_name: e.target.value }))} />
            </Form.Group>
            <Row className="g-2 mb-2">
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">โควตา/ปี (วัน)</Form.Label>
                <Form.Control size="sm" type="number" min="0.5" step="0.5" placeholder="เว้นว่าง = ไม่จำกัด"
                  value={modal?.lt_annual_quota ?? ''}
                  onChange={e => setModal(m => ({ ...m, lt_annual_quota: e.target.value }))} />
              </Col>
              <Col xs={6} className="d-flex flex-column justify-content-end pb-1">
                <Form.Check type="switch" id="lt-require-attachment" label="ต้องแนบไฟล์ประกอบ"
                  checked={!!modal?.lt_require_attachment}
                  onChange={e => setModal(m => ({ ...m, lt_require_attachment: e.target.checked ? 1 : 0 }))} />
                <Form.Check type="switch" id="lt-active" label="เปิดใช้งาน"
                  checked={!!modal?.lt_active}
                  onChange={e => setModal(m => ({ ...m, lt_active: e.target.checked ? 1 : 0 }))} />
              </Col>
            </Row>
            <Form.Group>
              <Form.Label className="fs-sm mb-1">หมายเหตุ</Form.Label>
              <Form.Control size="sm" as="textarea" rows={2} value={modal?.lt_note || ''}
                onChange={e => setModal(m => ({ ...m, lt_note: e.target.value }))} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setModal(null)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}บันทึก
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
